import { SemanticSearchMethod, VideoStatusCode } from 'utils/enum';
import { ILibraryItem } from 'utils/models';

interface SemanticSearchHit {
  chunk_id: string;
  mention_count?: number | null;
  media: {
    media_id: string;
    title: string;
    filename: string;
    media_type: string;
    thumbnail: string | null;
    video_thumbnail: string | null;
    duration_ms: number;
    created_at: number;
    updated_at: number;
    published_at?: number | null;
    status_code: number;
    // /library's status text for status_code; null for an unknown code.
    status?: string | null;
    speakers_name: string[];
    summary: string;
    speakers?: number;
    words?: number;
    publishcount?: number;
    viewed?: number;
    is_sensitive?: boolean | null;
    userid?: string | null;
  };
  location: {
    timing_precision: string;
    play_start_ms?: number;
    match_end_ms?: number;
    chunk_end_ms?: number;
  };
  highlight: {
    kind: string;
    snippet: string;
    spans: { start: number; end: number }[];
  };
}

// The engine's parse of the request, echoed back. Only the fields read here.
interface SemanticSearchQuery {
  // query_parser.py QueryType, e.g. "filter_only" when there is no free text.
  type?: string;
  raw?: string | null;
  free_text?: string | null;
  phrases?: unknown[] | null;
  negations?: unknown[] | null;
  filters?: Record<string, unknown> | null;
}

interface SemanticSearchResponse {
  query?: SemanticSearchQuery;
  results: SemanticSearchHit[];
  pagination: {
    limit: number;
    offset: number;
    pool_size: number;
    // null on the engine's browse path (no text, title search, date sort),
    // which has no lexical/semantic pools.
    lexical_count?: number | null;
    semantic_count?: number | null;
    // Set only on a filter-only page past the 10000 window with a next page.
    next_search_after?: unknown[] | null;
  };
}

interface MappedLibraryPayload {
  library: ILibraryItem[];
  page_number: number;
  count: number;
  total: {
    totalFiles: number;
    totalAudio: number;
    totalDurations: number;
    sharedItems: number;
  };
  // null when results are not split into exact and related (no search text,
  // or the engine browsed).
  semanticCounts: SemanticSearchCounts | null;
  // Cards at the head of `library` that are exact (lexical) matches; the rest
  // are related. null when results are not split.
  exactMatchOnPage: number | null;
  semanticQueryType: string | null;
  semanticFilterState: SemanticFilterState | null;
  // Read by the library reducer, which keeps it as the next page's cursor.
  next_search_after: unknown[] | null;
}

// No free text (blank search or title search): snippets are the clip's
// opening transcript, not a match.
export const SEMANTIC_FILTER_ONLY = 'filter_only';

// filter_only: no free text, at least one filter set.
// no_filter: the engine also says filter_only, but nothing is set at all.
export type SemanticFilterState = 'filter_only' | 'no_filter';

// false, 0-length and blank values are the engine's "not set" defaults
// (brand_safe: false, speakers: [], date: null).
const hasValue = (value: unknown): boolean => {
  if (value == null || value === false) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.some(hasValue);
  if (typeof value === 'object') return Object.values(value).some(hasValue);
  return true;
};

/** null unless the engine parsed the query as filter_only. Every key of
 * `filters` counts, so a filter the engine adds later needs no change here.
 * Sort is not part of `query`, so it never counts. */
export const resolveSemanticFilterState = (
  query?: SemanticSearchQuery,
): SemanticFilterState | null => {
  if (query?.type !== SEMANTIC_FILTER_ONLY) return null;
  const { raw, free_text, phrases, negations, filters } = query;
  return [raw, free_text, phrases, negations, filters].some(hasValue)
    ? 'filter_only'
    : 'no_filter';
};

export interface SemanticSearchCounts {
  all: number;
  lexical: number;
  semantic: number;
}

/** One card per hit, in `results` order. A media can appear on several cards
 * (the engine keeps up to max_per_media passages each), so every card carries
 * a `resultKey` for React keys and drag ids; `mediaid` stays the media's id. */
export const mapSemanticSearchResponse = (
  response: SemanticSearchResponse,
  searchMethod: SemanticSearchMethod = SemanticSearchMethod.ALL,
  hasSearchText = true,
): MappedLibraryPayload => {
  const library: ILibraryItem[] = [];
  const {
    limit = response?.results?.length || 1,
    offset = 0,
    pool_size = response?.results?.length ?? 0,
    lexical_count = 0,
    semantic_count = 0,
  } = response?.pagination ?? {};
  const isSplit =
    hasSearchText && lexical_count !== null && semantic_count !== null;
  let exactMatches = 0;

  (response?.results ?? []).forEach((hit, index) => {
    const mediaId = hit.media?.media_id;
    if (!mediaId) return;

    // In the all method the engine ranks every lexical hit ahead of the
    // semantic ones, so a hit's global position tells which pool it is from.
    if (offset + index < (lexical_count ?? 0)) exactMatches += 1;

    const statusCode = hit.media.status_code;
    library.push({
      mediaid: mediaId,
      resultKey: `${mediaId}#${hit.chunk_id}`,
      title: hit.media.title || hit.media.filename || '',
      filename: hit.media.filename,
      path: '',
      size: '',
      editor: '',
      length: String(hit.media.duration_ms ?? 0),
      mediacontenttype: hit.media.media_type,
      mediatype: hit.media.media_type,
      statuscode: statusCode as VideoStatusCode,
      // No text for the code: /library sends null too; both mean no badge
      // and no lock.
      status: hit.media.status ?? '',
      thumbnail: hit.media.thumbnail ?? '',
      video_thumbnail: hit.media.video_thumbnail,
      publishcount: hit.media.publishcount ?? 0,
      words: String(hit.media.words ?? ''),
      // A count, like the library's own `speakers`; names are in speakers_name.
      speakers: String(hit.media.speakers ?? ''),
      summary: hit.media.summary ?? '',
      createddatetime: new Date(hit.media.created_at ?? 0),
      modifieddatetime: new Date(hit.media.updated_at ?? 0),
      publisheddatetime: hit.media.published_at
        ? new Date(hit.media.published_at)
        : '',
      userid: hit.media.userid ?? '',
      noTranscript: false,
      viewed: hit.media.viewed ?? 0,
      clips_count: 0,
      is_sensitive: hit.media.is_sensitive ?? false,
      sensitive_items: '',
      highlight: hit.highlight
        ? {
            kind: hit.highlight.kind,
            snippet: hit.highlight.snippet,
            spans: hit.highlight.spans ?? [],
          }
        : undefined,
      mention_count: hit.mention_count ?? null,
      timing_precision: hit.location?.timing_precision,
      play_start_ms: hit.location?.play_start_ms,
      match_end_ms: hit.location?.match_end_ms,
      chunk_end_ms: hit.location?.chunk_end_ms,
    });
  });

  const count = pool_size;
  const allCount =
    lexical_count == null && semantic_count == null
      ? pool_size
      : (lexical_count ?? 0) + (semantic_count ?? 0);

  return {
    library,
    page_number: Math.floor(offset / limit) + 1,
    count,
    total: {
      totalFiles: count,
      totalAudio: library.filter((item) =>
        item.mediacontenttype.startsWith('audio'),
      ).length,
      totalDurations: library.reduce(
        (sum, item) => sum + Number(item.length || 0),
        0,
      ),
      sharedItems: 0,
    },
    semanticCounts: isSplit
      ? {
          all: allCount,
          lexical: lexical_count ?? 0,
          semantic: semantic_count ?? 0,
        }
      : null,
    exactMatchOnPage: !isSplit
      ? null
      : {
          [SemanticSearchMethod.ALL]: exactMatches,
          [SemanticSearchMethod.LEXICAL]: library.length,
          [SemanticSearchMethod.SEMANTIC]: 0,
        }[searchMethod],
    semanticQueryType: response?.query?.type ?? null,
    semanticFilterState: resolveSemanticFilterState(response?.query),
    next_search_after: response?.pagination?.next_search_after ?? null,
  };
};
